import "./RevAudReport.css";

import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { Fragment, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "src/components/shared/Button/Button";
import { useGetApi, usePostApi, useTheme } from "src/hooks";
import { createPdfHead } from "src/mock/createPdfHead";
import { useSelector } from "src/redux/hooks";
import { ButtonsWrapper, PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import styles from "./RevAudReport.css?inline";
import { mapReviewReport } from "./RevAudReport.utils";

const ReviewReport = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { uppercase } = useSelector(state => state.preferencesSlice);
  const ref = useRef<HTMLDivElement>(null);
  const { aid, yachtid } = useParams();
  const { post, isLoading } = usePostApi();

  const { data } = useGetApi({
    request: "create-review-report",
    body: { params: { aid, yachtid } },
    mapper: mapReviewReport,
  });

  const handlePdf = useCallback(async () => {
    if (!data) return;
    const report = ref.current?.cloneNode(true) as HTMLDivElement;
    const style = document.createElement("style");
    style.innerHTML = styles;
    report.appendChild(style);

    try {
      const name = `Report ${data.type}${data.date ? ` ${data.date}` : ""}`;
      const fileData = await post({
        request: "create-pdf",
        body: {
          params: {
            name,
            head: createPdfHead,
            yachtid: getYachtId(),
            reviewdate: data.date,
            body: report.outerHTML,
          },
        },
      });

      if (window.api) {
        window.api.downloadFile(
          fileData.file,
          ({ full, path }) => {
            notifications.show({
              title: "Report Downloaded",
              message: (
                <div>
                  <Button
                    variant="subtle"
                    compact
                    onClick={() => {
                      if (window.api) window.api.openFile(full);
                    }}>
                    Open File
                  </Button>
                  <Button
                    variant="subtle"
                    compact
                    onClick={() => {
                      if (window.api) window.api.openFile(path);
                    }}>
                    Open Folder
                  </Button>
                </div>
              ),
            });
          },
          "reviews",
        );
      } else {
        window.open(fileData.file, "_blank");
      }
    } catch {
      Notification(
        "Error",
        "Something went wrong. Try to re-run the report or try again later",
        "red",
      );
    }
  }, [data, post]);

  const handleNavigate = useCallback(
    (entry: { meid?: string; partid?: string; lbeid: string }) => () => {
      const isAudit = pathname.includes("audits/audit");
      const idType = entry.meid ? "meid" : entry.partid ? "partid" : "lbeid";
      const id = entry.meid ?? entry.partid ?? entry.lbeid;
      const path = `/summary/${
        isAudit ? "audits/audit" : "reviews/review"
      }/${aid}/${idType}/${id}`;
      const search = `?yachtid=${yachtid}&yachtuser=${data?.yachtuser ? "1" : "0"}`;

      if (window.api) {
        navigate({ pathname: path, search });
      } else {
        window.open(`/#${path}${search}`, "_blank");
      }
    },
    [aid, navigate, pathname, yachtid, data?.yachtuser],
  );

  const theme = useTheme();
  const main = theme.colors?.main ? theme.colors.main[9] : "";
  return (
    <PageContainer>
      <Breadcrumbs
        customLinks={!data?.yachtuser ? links => links.splice(2) : undefined}
        border={false}>
        <ButtonsWrapper>
          <Button onClick={handlePdf} loading={isLoading}>
            PDF
          </Button>
        </ButtonsWrapper>
      </Breadcrumbs>

      <div ref={ref} style={{ width: 1080, marginTop: 20 }}>
        <div className="frame" style={{ border: `2px solid ${main}` }}>
          <div className="frame-header" style={{ backgroundColor: main }}>
            <p className="frame-header-title">{data?.sigtype}</p>
            <p className="frame-header-date">
              {`${data?.type !== "Audit" ? "Review Date:" : "Audit Date:"} ${
                data?.date
                  ? dayjs(data.date).format("DD-MMM-YYYY - HH:mm")
                  : "Not Completed"
              }`}
            </p>
          </div>
          <div className="frame-content">
            <p>{data?.reviewer}</p>
          </div>
        </div>

        <div className="frame" style={{ border: `2px solid ${main}` }}>
          <div className="frame-header" style={{ backgroundColor: main }}>
            <p className="frame-header-title">SUMMARY COMMENTS</p>
          </div>
          <div className="frame-content">
            {data?.comments.length === 0 && <p>No Comments...</p>}
            {data?.comments.length > 0 &&
              data?.comments.map((comment: any) => (
                <div key={comment.header} className="comment">
                  <p className="comment-header">{comment.header}</p>
                  <p
                    className="comment-content"
                    style={{ textTransform: uppercase ? "uppercase" : "none" }}>
                    {comment.content}
                  </p>
                </div>
              ))}
          </div>
        </div>

        <div className="frame" style={{ border: `2px solid ${main}` }}>
          <div className="frame-header" style={{ backgroundColor: main }}>
            <p className="frame-header-title">ENTRIES</p>
          </div>
          <table className="entries-table">
            <thead>
              <tr
                className="entries-table-head-row"
                style={{ backgroundColor: main }}>
                <th>
                  <p>Entry Ref</p>
                </th>
                <th>
                  <p>Logbook Title</p>
                </th>
                <th>
                  <p>Entry Date</p>
                </th>
                <th>
                  <p>Entry Status</p>
                </th>
              </tr>
            </thead>
            {data?.entries.length > 0 && (
              <tbody>
                {data?.entries.map((entry: any) => (
                  <Fragment key={entry.ref}>
                    <tr
                      onClick={handleNavigate(entry)}
                      className="row-entry"
                      style={{ borderTop: `1px solid ${main}` }}>
                      <td>
                        <p>{entry.ref}</p>
                      </td>
                      <td>
                        <p>{entry.title}</p>
                      </td>
                      <td>
                        <p>{entry.date}</p>
                      </td>
                      <td>
                        <p>{entry.status}</p>
                      </td>
                    </tr>
                    {entry.comments.length > 0 && (
                      <tr className="row-entry-comments">
                        <td colSpan={4}>
                          <div>
                            {entry.comments.map((comment: any) => (
                              <div
                                key={comment.header}
                                className="comment"
                                style={{ borderBottom: `1px solid ${main}` }}>
                                <p
                                  className="comment-header"
                                  style={{ color: main }}>
                                  {comment.header}
                                </p>
                                <p className="comment-content">
                                  {comment.content}
                                </p>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
          {data?.entries.length === 0 && (
            <div className="frame-content">
              <p>No Entries...</p>
            </div>
          )}
        </div>

        <div className="frame" style={{ border: `2px solid ${main}` }}>
          <div className="frame-header" style={{ backgroundColor: main }}>
            <p className="frame-header-title">{`${data?.sigtype} SIGNATURE`}</p>
          </div>
          <div className="frame-content sign-box"></div>
        </div>

        <div className="frame" style={{ border: `2px solid ${main}` }}>
          <div className="frame-header" style={{ backgroundColor: main }}>
            <p className="frame-header-title">CAPTAIN/MASTER SIGNATURE</p>
          </div>
          <div className="frame-content sign-box"></div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ReviewReport;
